import { useState, useEffect, useRef, useCallback } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { AiOutlineReload } from 'react-icons/ai';
import { FiActivity } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useToast } from "@/components/ui/use-toast";
import { MonitorListCard } from '@/components/MonitorListCard';
import { useAiPageContext } from '@/context/AiPageContextProvider';
import { contextHelpers } from "@/components/AiCommandPalette";

const Monitors = () => {
    const [loading, setLoading] = useState(true);
    const [monitors, setMonitors] = useState([]);
    const [refreshingIds, setRefreshingIds] = useState(new Set());
    const navigate = useNavigate();
    const { axiosInstance } = useAuth();
    const { toast } = useToast();
    const { setCurrentAiPageContext } = useAiPageContext();
    
    // Track polling intervals for each monitor
    const pollingIntervalsRef = useRef({});
    // Track last known run IDs for comparison
    const lastRunDataRef = useRef({});

    const fetchMonitors = async (showLoading = true) => {
        if (showLoading) {
            setLoading(true);
        }
        try {
            const response = await axiosInstance.get('/monitors');
            setMonitors(response.data);
            
            // Store initial run data for comparison AND start polling running monitors
            response.data.forEach(m => {
                if (m.lastRun) {
                    lastRunDataRef.current[m.id] = {
                        runId: m.lastRun.runId,
                        endTime: m.lastRun.endTime,
                        msUntilNextRun: m.nextRun?.msUntilNextRun
                    };
                    
                    // If monitor is running (no endTime), start polling it
                    if (!m.lastRun.endTime) {
                        startPollingMonitor(m.id);
                    }
                }
            });
        } catch (error) {
            console.error('Error fetching monitors:', error);
            toast({
                title: "Error fetching monitors",
                variant: "destructive",
            });
        } finally {
            if (showLoading) {
                setLoading(false);
            }
        }
    };

    // Stop polling a specific monitor
    const stopPollingMonitor = useCallback((monitorId) => {
        if (pollingIntervalsRef.current[monitorId]) {
            clearInterval(pollingIntervalsRef.current[monitorId]);
            delete pollingIntervalsRef.current[monitorId];
            
            setRefreshingIds(prev => {
                const next = new Set(prev);
                next.delete(monitorId);
                return next;
            });
        }
    }, []);

    // Poll a specific monitor for updates
    const pollMonitor = useCallback(async (monitorId) => {
        try {
            const response = await axiosInstance.get(`/monitors/${monitorId}/poll`);
            const pollData = response.data;
            
            const lastKnownData = lastRunDataRef.current[monitorId];
            
            // Check if run data changed (runId changed OR endTime changed)
            const runIdChanged = lastKnownData?.runId !== pollData.lastRun?.runId;
            const endTimeChanged = lastKnownData?.endTime !== pollData.lastRun?.endTime;
            
            if (runIdChanged || endTimeChanged) {
                // Update the monitor in our state with fresh data from server
                setMonitors(prev => prev.map(m => {
                    if (m.id === monitorId) {
                        return {
                            ...m,
                            lastRun: pollData.lastRun,
                            nextRun: pollData.nextRun  // Fresh countdown from server
                        };
                    }
                    return m;
                }));
                
                // Update our tracking ref
                lastRunDataRef.current[monitorId] = {
                    runId: pollData.lastRun?.runId,
                    endTime: pollData.lastRun?.endTime,
                    msUntilNextRun: pollData.nextRun?.msUntilNextRun
                };
            }
            
            // Stop polling when run is complete (has endTime)
            if (pollData.lastRun?.endTime) {
                stopPollingMonitor(monitorId);
            }
        } catch (error) {
            console.error(`Error polling monitor ${monitorId}:`, error);
        }
    }, [axiosInstance, stopPollingMonitor]);

    // Start polling a specific monitor
    const startPollingMonitor = useCallback((monitorId) => {
        // Don't start if already polling
        if (pollingIntervalsRef.current[monitorId]) {
            return;
        }
        
        setRefreshingIds(prev => new Set([...prev, monitorId]));
        
        // Poll immediately, then every 3 seconds
        pollMonitor(monitorId);
        pollingIntervalsRef.current[monitorId] = setInterval(() => {
            pollMonitor(monitorId);
        }, 3000);
    }, [pollMonitor]);

    // Handle countdown expiring from a card
    const handleCountdownExpired = useCallback((monitorId) => {
        startPollingMonitor(monitorId);
    }, [startPollingMonitor]);

    // Run a monitor check immediately
    const runMonitorNow = async (monitor) => {
        const monitorId = monitor.id;
        try {
            setRefreshingIds(prev => new Set([...prev, monitorId]));
            
            await axiosInstance.post(`/monitors/${monitorId}/run`);
            // Start polling for the result
            startPollingMonitor(monitorId);
        } catch (error) {
            console.error('Error running monitor:', error);
            toast({
                title: "Failed to run monitor check",
                variant: "destructive",
            });
            setRefreshingIds(prev => {
                const next = new Set(prev);
                next.delete(monitorId);
                return next;
            });
        }
    };

    // Initial fetch
    useEffect(() => {
        fetchMonitors();
    }, []);

    // Cleanup polling intervals on unmount
    useEffect(() => {
        return () => {
            Object.values(pollingIntervalsRef.current).forEach(clearInterval);
        };
    }, []);

    // Set AI page context for monitors list
    useEffect(() => {
        setCurrentAiPageContext(
            contextHelpers.createCustomContext(
                'monitors',
                'Monitors',
                {
                    totalMonitors: monitors.length,
                    activeMonitors: monitors.filter(m => m.isActive).length,
                    downMonitors: monitors.filter(m => !m.lastRun?.success && m.isActive).length
                }
            )
        );
    }, [monitors, setCurrentAiPageContext]);

    // Refresh monitors when tab becomes visible
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible' && !loading) {
                // Background refresh to get latest data without showing skeleton
                fetchMonitors(false);
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [loading]);

    const handleMonitorClick = (monitor) => {
        navigate(`/monitors/${monitor.id}`);
    };

    return (
        <Layout>
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="mb-8"
            >
                <Card>
                    <CardHeader className="flex flex-row justify-between">
                        <CardTitle className="text-3xl font-bold">Monitors</CardTitle>
                        <div className="flex space-x-4">
                            <Button variant="ghost" size="icon" onClick={() => fetchMonitors()}>
                                <AiOutlineReload className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
                            </Button>
                            <Button onClick={() => navigate('/monitors/new')}>
                                Add Monitor
                            </Button>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {[...Array(6)].map((_, i) => (
                                    <Card key={i} className="h-[190px]">
                                        <CardContent className="p-4">
                                            <div className="space-y-3">
                                                {/* Title and badge */}
                                                <div className="flex items-start justify-between gap-3 mb-4">
                                                    <Skeleton className="h-6 w-1/2 mt-1" />
                                                    <Skeleton className="h-7 w-1/3 rounded-full" />
                                                </div>
                                                
                                                {/* Type & Organization */}
                                                <Skeleton className="h-3 w-1/2" />
                                                
                                                {/* URL */}
                                                <Skeleton className="h-3 w-3/4" />
                                                
                                                {/* Last check info */}
                                                <div className="flex justify-between items-center">
                                                    <Skeleton className="h-3 w-2/5" />
                                                    <Skeleton className="h-3 w-12" />
                                                </div>
                                                
                                                {/* Buttons */}
                                                <div className="flex justify-between items-center pt-3">
                                                    <div className="flex space-x-2">
                                                        <Skeleton className="h-7 w-24" />
                                                        <Skeleton className="h-7 w-16" />
                                                    </div>
                                                    <Skeleton className="h-7 w-9" />
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        ) : monitors.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {monitors.map((monitor) => (
                                    <MonitorListCard 
                                        monitor={monitor}
                                        onClick={() => handleMonitorClick(monitor)}
                                        onRunNow={runMonitorNow}
                                        onCountdownExpired={handleCountdownExpired}
                                        isRefreshing={refreshingIds.has(monitor.id)}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-16">
                                <div className="mx-auto h-32 w-32 bg-muted/50 rounded-full flex items-center justify-center mb-6">
                                    <FiActivity className="h-16 w-16 text-muted-foreground" />
                                </div>
                                <h3 className="text-2xl font-semibold text-foreground mb-3">No monitors found</h3>
                                <p className="text-muted-foreground mb-8 max-w-md mx-auto">Get started by creating your first monitor to track uptime and performance of your services.</p>
                                <Button 
                                    onClick={() => navigate('/monitors/new')}
                                    className="px-8 py-3 text-lg font-semibold"
                                >
                                    Create Your First Monitor
                                </Button>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </motion.div>
        </Layout>
    );
};

export default Monitors;
